/*
 * Shared shipping-banner helper: location-keyed localStorage cache plus
 * the announcement-bar cell injector. Single source of truth used by
 * both the early inline apply (in theme.liquid, runs before paint) and
 * the deferred sync scripts.
 *
 * DISPLAY TEXT:
 *   The geolocation app prints a localized free-shipping sentence with the
 *   market's converted threshold, e.g. "Gratis frakt till Sverige på alla
 *   beställningar över 1620 kr" or "...สูงกว่า ฿7575". We do NOT mirror that
 *   sentence verbatim — we extract the number and re-format it ourselves as
 *   "<amount> <CURRENCY CODE>", rendering a single per-language message like
 *   "Free Shipping On Orders Over 1,620 SEK". The currency ISO code always
 *   follows the amount, for every market. The RAW banner text is still
 *   stamped on [data-shipping-banner-synced-value] so
 *   sync-free-shipping-threshold.js can keep parsing the threshold number
 *   for the cart bar.
 *
 * HIDING THE SOURCE BANNER:
 *   #FreeShippingBanner (the grey app banner) is only a data source. We hide it
 *   with an injected !important stylesheet rule rather than an inline style,
 *   because the app re-renders the banner and resets its inline style, which is
 *   why it kept reappearing above the real announcement bar. display:none does
 *   not stop us reading its textContent.
 */
(function () {
  var STORAGE_PREFIX = 'shipping-banner:';
  var MESSAGE_LIST_SELECTOR = 'announcement-bar .announcement-bar__message-list';
  var SYNCED_ATTR = 'data-shipping-banner-synced-value';
  var SOURCE_SELECTOR = '#FreeShippingBanner';

  // The copy shown in the announcement bar, per language. {amount} is replaced
  // with the market-specific value parsed from the app banner (e.g. "1620 kr").
  // The language is taken from <html lang> (Shopify sets it per locale), so the
  // text follows the language the shopper selected. Edit these strings freely.
  var MESSAGES = {
    en: 'Free Shipping On Orders Over {amount}',
    es: 'Envío gratis en pedidos superiores a {amount}',
    pt: 'Envio grátis em encomendas acima de {amount}'
  };
  var DEFAULT_LANG = 'en';

  // Primary language subtag of the current page, e.g. "en", "es", "pt"
  // (from "pt-PT", "es-ES", ...). Read live so a locale switch (which reloads
  // the page with a new <html lang>) picks up the right copy.
  function currentLang() {
    var lang = '';
    try { lang = document.documentElement.getAttribute('lang') || ''; } catch (e) {}
    return (lang.split('-')[0] || DEFAULT_LANG).toLowerCase();
  }

  // Hide the raw app banner everywhere, robustly. Injected as early as this
  // (head, blocking) script runs so it applies from first paint.
  (function hideSourceBanner() {
    try {
      var STYLE_ID = 'shipping-banner-hide';
      if (document.getElementById(STYLE_ID)) return;
      var style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = SOURCE_SELECTOR + '{display:none !important;}';
      (document.head || document.documentElement).appendChild(style);
    } catch (e) {}
  })();

  function locationKey() {
    var country = '';
    var currency = '';
    try {
      if (window.Shopify) {
        country = window.Shopify.country || '';
        if (window.Shopify.currency && window.Shopify.currency.active) {
          currency = window.Shopify.currency.active;
        }
      }
    } catch (e) {}
    return STORAGE_PREFIX + country + ':' + currency;
  }

  function get() {
    try {
      return window.localStorage.getItem(locationKey()) || null;
    } catch (e) {
      return null;
    }
  }

  function set(text) {
    if (!text) return;
    try {
      window.localStorage.setItem(locationKey(), text);
    } catch (e) {}
  }

  // Pull the "amount + currency" out of the localized banner sentence:
  //   "...over 1620 kr"            -> "1620 kr"
  //   "...above THB symbol 7575"   -> "<symbol>7575"
  //   "...over 200 EUR"            -> "200 EUR"
  //   "...over <symbol>200 to ..." -> "<symbol>200"
  //   "...over 1 620,00 kr"        -> "1 620,00 kr"
  // A fallback only (Intl formatting is preferred); returns '' if nothing found.
  function extractAmount(text) {
    if (!text) return '';
    // Normalise non-breaking / thin spaces to a plain space.
    var t = text.replace(/[   ]/g, ' ');

    // Last number token (handles thousands grouping and decimals).
    var re = /\d[\d.,\s]*\d|\d/g;
    var m, start = -1, end = -1;
    while ((m = re.exec(t)) !== null) {
      start = m.index;
      end = m.index + m[0].length;
    }
    if (start === -1) return '';
    var num = t.slice(start, end);
    var before = t.slice(0, start);

    var sym;
    try { sym = /([A-Za-z]{0,3}\p{Sc}+)\s?$/u.exec(before); }
    catch (e) { sym = /([A-Za-z]{0,3}[$€£¥₩₹฿₽]+)\s?$/.exec(before); }
    if (sym) return t.slice(sym.index, end).replace(/\s+/g, ' ').trim();

    var suf = t.slice(end).match(/^\s*([A-Za-zÀ-ʏ]{1,4})/);
    if (suf) return (num + ' ' + suf[1]).replace(/\s+/g, ' ').trim();

    var code = /([A-Z]{2,4})\s$/.exec(before);
    if (code) return (code[1] + ' ' + num).replace(/\s+/g, ' ').trim();

    return num.trim();
  }

  // Numeric (major-unit) value from the banner text, or null. Handles thousands
  // grouping (".", ",", spaces) and a trailing 1-2 digit decimal group.
  function parseNumber(text) {
    if (!text) return null;
    var t = text.replace(/[   ]/g, ' ');
    var tokens = t.match(/\d[\d.,\s]*\d|\d/g);
    if (!tokens || !tokens.length) return null;
    var tok = tokens[tokens.length - 1].replace(/\s/g, '');
    var lastSep = Math.max(tok.lastIndexOf('.'), tok.lastIndexOf(','));
    var val;
    if (lastSep === -1) {
      val = parseFloat(tok);
    } else {
      var dec = tok.slice(lastSep + 1);
      if (dec.length === 1 || dec.length === 2) {
        val = parseFloat(tok.slice(0, lastSep).replace(/[.,]/g, '') + '.' + dec);
      } else {
        val = parseFloat(tok.replace(/[.,]/g, ''));
      }
    }
    return isNaN(val) ? null : val;
  }

  // The market currency code Shopify is rendering, e.g. "UZS", "EUR", "SEK".
  function activeCurrency() {
    try { if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) return window.Shopify.currency.active; } catch (e) {}
    try { if (window.MIMOA_SHIPPING && window.MIMOA_SHIPPING.currency) return window.MIMOA_SHIPPING.currency; } catch (e) {}
    return '';
  }

  // The amount shown in the message. Primary path: format the parsed number as a
  // plain grouped number in the current page language, then append the market
  // currency ISO code AFTER it — e.g. "1,620 SEK", "1,620 EUR", "1,620 USD".
  // Uniform "amount then code" for every market. Falls back to lifting the
  // amount straight out of the banner text only if the value or Intl is
  // unavailable.
  function formatAmount(rawText) {
    var value = parseNumber(rawText);
    var currency = activeCurrency();
    if (value != null && currency) {
      try {
        var num = new Intl.NumberFormat(currentLang(), {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(value);
        return num + ' ' + currency;
      } catch (e) {}
    }
    return extractAmount(rawText) || (value != null ? String(value) : '');
  }

  // The message to display, built from the raw banner text and the current
  // language. Falls back to the raw text if no amount can be parsed.
  function formatMessage(rawText) {
    var amount = formatAmount(rawText);
    if (!amount) return rawText;
    var template = MESSAGES[currentLang()] || MESSAGES[DEFAULT_LANG];
    return template.replace('{amount}', amount);
  }

  function buildCell(messageList, rawText) {
    var template = messageList.querySelector('.prose');
    var baseClass = template
      ? template.className.replace(/\bis-selected\b/g, '').trim()
      : 'prose prose-justify-center';

    var wrapper = document.createElement('div');
    wrapper.className = baseClass + ' is-selected';
    // Keep the RAW banner text on the attribute — the cart threshold parser
    // reads it. The visible <p> shows the formatted message.
    wrapper.setAttribute(SYNCED_ATTR, rawText);

    var p = document.createElement('p');
    var templateP = template ? template.querySelector('p') : null;
    p.className = templateP ? templateP.className : 'subheading subheading-xs';
    p.textContent = formatMessage(rawText);

    wrapper.appendChild(p);
    return wrapper;
  }

  function injectAnnouncementCell(rawText) {
    if (!rawText) return false;
    var messageList = document.querySelector(MESSAGE_LIST_SELECTOR);
    if (!messageList) return false;

    var display = formatMessage(rawText);
    var existing = messageList.querySelector('[' + SYNCED_ATTR + ']');
    if (existing) {
      var p = existing.querySelector('p');
      if (p && p.textContent.trim() !== display) {
        p.textContent = display;
      }
      if (existing.getAttribute(SYNCED_ATTR) !== rawText) {
        existing.setAttribute(SYNCED_ATTR, rawText);
      }
      return 'updated';
    }

    Array.prototype.forEach.call(messageList.children, function (child) {
      child.classList.remove('is-selected');
    });
    messageList.insertBefore(buildCell(messageList, rawText), messageList.firstChild);
    return 'created';
  }

  window.ShippingBannerCache = {
    get: get,
    set: set,
    key: locationKey,
    injectAnnouncementCell: injectAnnouncementCell,
    formatMessage: formatMessage,
    extractAmount: extractAmount,
    parseNumber: parseNumber,
    SYNCED_ATTR: SYNCED_ATTR,
  };
})();
